<template>
  <div class="cobranca-detail">
    <div v-if="!boletosOperacionais" class="alert alert-info" role="status">Operações de boletos desabilitadas para esta escola. O histórico permanece disponível.</div>
    <div
      class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
    >
      <div>
        <h5 class="mb-1">
          Detalhes da Cobrança
          <span v-if="cobranca">
            #{{ cobranca.id }}
          </span>
        </h5>

        <small class="text-muted">
          Dados bancários, boleto e histórico do processamento.
        </small>
      </div>

      <div class="d-flex flex-wrap align-items-center gap-2">
        <button v-if="podeOperar && cobranca?.status === 'RASCUNHO' && !cobranca?.beneficiario_documento"
          type="button" class="btn btn-outline-primary btn-sm" :disabled="loading || operacaoEmAndamento" @click="capturarBeneficiario">
          Conferir e preservar beneficiário
        </button>
        <button
          v-if="podeRegistrar"
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="registrando || loading"
          @click="registrarCobranca"
        >
          <span
            v-if="registrando"
            class="spinner-border spinner-border-sm me-1"
            role="status"
            aria-hidden="true"
          ></span>

          <i
            v-else
            class="bi bi-send-check me-1"
          ></i>

          {{ textoBotaoRegistro }}
        </button>

        <button
          v-if="podeEnviarEmail"
          type="button"
          class="btn btn-outline-primary btn-sm"
          :disabled="operacaoEmAndamento || loading"
          @click="abrirPreviewEmail"
        >
          <span
            v-if="carregandoPreviewEmail"
            class="spinner-border spinner-border-sm me-1"
            role="status"
            aria-hidden="true"
          ></span>

          <i
            v-else
            class="bi bi-envelope-check me-1"
          ></i>

          {{ textoBotaoEmail }}
        </button>

        <div v-if="exibirAcaoReemissao" class="d-flex flex-column align-items-start gap-1"
          role="group" :aria-label="textoReemissao"
          :tabindex="!podeReemitir ? 0 : undefined"
          :aria-describedby="!podeReemitir ? 'reemissao-indisponivel' : undefined">
          <button type="button" class="btn btn-outline-primary btn-sm"
            :disabled="!podeReemitir || operacaoEmAndamento || loading"
            :aria-describedby="!podeReemitir ? 'reemissao-indisponivel' : undefined"
            @click="abrirReemissao">
            {{ textoReemissao }}
          </button>
          <small v-if="!podeReemitir" id="reemissao-indisponivel" class="text-muted" style="max-width: 24rem">
            {{ reemissao.motivo }}
          </small>
        </div>

        <button
          v-if="podeCancelar"
          type="button"
          class="btn btn-outline-danger btn-sm"
          :disabled="operacaoEmAndamento || loading"
          @click="cancelarCobranca"
        >
          <span
            v-if="cancelando"
            class="spinner-border spinner-border-sm me-1"
            role="status"
            aria-hidden="true"
          ></span>

          <i
            v-else
            class="bi bi-x-octagon me-1"
          ></i>

          {{ textoBotaoCancelamento }}
        </button>

        <button
          type="button"
          class="btn btn-secondary btn-sm"
          :disabled="operacaoEmAndamento"
          @click="voltar"
        >
          <i class="bi bi-arrow-left me-1"></i>
          Voltar
        </button>
      </div>
    </div>

    <div v-if="reemissaoModalAberto && cobranca" class="modal d-block" role="dialog" aria-modal="true" aria-labelledby="reemissao-title" style="background: #0008">
      <div class="modal-dialog modal-dialog-centered"><form class="modal-content" @submit.prevent="confirmarReemissao">
        <div class="modal-header"><h5 id="reemissao-title" class="modal-title">{{ textoReemissao }}</h5></div>
        <div class="modal-body">
          <p>Boleto atual: {{ formatDate(cobranca.data_vencimento_atual) }} · {{ formatMoney(cobranca.valor_atual) }} · {{ statusLabel(cobranca.status) }}</p>
          <p v-if="cobranca.status === 'REGISTRADA'">O boleto atual será cancelado. Após a confirmação do cancelamento, será criada uma nova cobrança em rascunho para a mesma obrigação, com o novo vencimento e sem juros/multa.</p>
          <p v-else>Será criada uma nova cobrança em rascunho para a mesma obrigação, com o novo vencimento e sem juros/multa.</p>
          <label for="novo-vencimento" class="form-label">Novo vencimento *</label>
          <input id="novo-vencimento" v-model="novoVencimento" type="date" class="form-control" required :min="reemissao.data_atual" :disabled="reemitindo" />
          <p class="mt-3">✓ Sem juros<br />✓ Sem multa</p>
          <label for="motivo-reemissao" class="form-label">Motivo / observação</label>
          <textarea id="motivo-reemissao" v-model="motivoReemissao" class="form-control" maxlength="1000" :disabled="reemitindo" placeholder="Negociação de novo vencimento com o responsável"></textarea>
          <div v-if="erroReemissao" class="alert alert-danger mt-3" role="alert">{{ erroReemissao }}</div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" :disabled="reemitindo" @click="reemissaoModalAberto = false">Cancelar</button>
          <button type="submit" class="btn btn-primary" :disabled="reemitindo || !novoVencimento">{{ reemitindo ? 'Processando...' : textoReemissao }}</button>
        </div>
      </form></div>
    </div>

    <div
      v-if="loading"
      class="card card-body shadow-sm py-5 text-center"
    >
      <div>
        <div
          class="spinner-border spinner-border-sm text-primary"
          role="status"
        ></div>
      </div>

      <div class="mt-2 text-muted">
        Carregando cobrança...
      </div>
    </div>

    <div
      v-else-if="errorMessage"
      class="alert alert-danger"
      role="alert"
    >
      {{ errorMessage }}
    </div>

    <div v-if="feedbackReemissao" class="alert alert-info" role="status">{{ feedbackReemissao }}</div>
    <template v-if="!loading && !errorMessage && cobranca">
      <FormSection v-if="reemissao?.substituta_id || reemissao?.original_id" title="Substituição">
        <p v-if="reemissao.substituta_id">
          Esta cobrança foi substituída pela cobrança #{{ reemissao.substituta_id }}.
          <router-link :to="{ name: 'DetalharCobranca', params: { id: reemissao.substituta_id } }">Ver cobrança substituta</router-link>
        </p>
        <p v-if="reemissao.original_id">
          Esta cobrança substitui a cobrança #{{ reemissao.original_id }}.
          <router-link :to="{ name: 'DetalharCobranca', params: { id: reemissao.original_id } }">Ver cobrança original</router-link>
          <span class="d-block">Sem juros · Sem multa</span>
        </p>
      </FormSection>
      <FormSection title="Resumo da cobrança">
        <div class="details-grid">
          <div class="detail-item detail-item-status">
            <span class="detail-label">
              Status
            </span>

            <span
              class="status-badge"
              :class="statusClass(cobranca.status)"
            >
              {{ statusLabel(cobranca.status) }}
            </span>
          </div>

          <DetailValue
            label="Instrumento"
            :value="formatInstrument(cobranca.tipo_instrumento)"
          />

          <DetailValue
            label="Aluno"
            :value="cobranca.aluno_nome"
          />

          <DetailValue
            label="Pagador"
            :value="cobranca.pagador_nome"
          />

          <DetailValue
            label="Contrato"
            :value="cobranca.numero_contrato"
          />

          <DetailValue
            label="Matrícula"
            :value="idLabel(cobranca.matricula_id)"
          />

          <DetailValue
            label="Origem"
            :value="originLabel(cobranca.origem_tabela)"
          />

          <DetailValue
            label="Pagamento de origem"
            :value="idLabel(cobranca.origem_pagamento_id)"
          />

          <DetailValue
            label="Valor original"
            :value="formatMoney(cobranca.valor_original)"
            emphasis
          />

          <DetailValue
            label="Valor atual"
            :value="formatMoney(cobranca.valor_atual)"
            emphasis
          />

          <DetailValue
            label="Valor pago"
            :value="optionalMoney(cobranca.valor_pago)"
          />

          <DetailValue
            label="Tarifa"
            :value="optionalMoney(cobranca.valor_tarifa)"
          />

          <DetailValue
            label="Vencimento original"
            :value="formatDate(cobranca.data_vencimento_original)"
          />

          <DetailValue
            label="Vencimento atual"
            :value="formatDate(cobranca.data_vencimento_atual)"
          />

          <DetailValue
            label="Emissão"
            :value="formatDateTime(cobranca.data_emissao)"
          />

          <DetailValue
            label="Registro"
            :value="formatDateTime(cobranca.data_registro)"
          />

          <DetailValue
            label="Pagamento pelo pagador"
            :value="formatDate(cobranca.data_pagamento_pagador)"
          />

          <DetailValue
            label="Liquidação"
            :value="formatDate(cobranca.data_liquidacao)"
          />

          <DetailValue
            label="Crédito"
            :value="formatDate(cobranca.data_credito)"
          />

          <DetailValue
            label="Identificador externo"
            :value="cobranca.identificador_externo"
            wide
            mono
          />

          <DetailValue
            label="Versão"
            :value="cobranca.versao"
          />

          <DetailValue
            label="Atualizado em"
            :value="formatDateTime(cobranca.atualizado_em)"
          />
        </div>
      </FormSection>

      <FormSection v-if="boleto?.sicredi_conferencia_json" title="Condições SICREDI">
        <p>Modalidade solicitada: {{ boleto.sicredi_conferencia_json.solicitado.tipo_cobranca === 'HIBRIDO' ? 'Híbrido (boleto + Pix)' : 'Tradicional' }}.</p>
        <p>Mora solicitada: {{ boleto.sicredi_conferencia_json.solicitado.juros_percentual_dia }}% ao dia.
          Multa percentual única: {{ boleto.sicredi_conferencia_json.solicitado.multa_percentual ?? '0.00' }}%.</p>
        <p>{{ boleto.sicredi_conferencia_json.estado === 'REGISTRO_ACEITO_NAO_CONFERIDO'
          ? 'Registro bancário aceito; condições ainda não conferidas por consulta.'
          : 'Taxas conferidas por consulta; regras de incidência e arredondamento pendentes.' }}</p>
        <p v-if="boleto.sicredi_conferencia_json.consultado">Validade Pix após vencimento informada na consulta:
          {{ boleto.sicredi_conferencia_json.consultado.validade_apos_vencimento_dias ?? 'não informada' }} dias.</p>
        <p>Início dos encargos, arredondamento e validade aplicável ao beneficiário ainda exigem confirmação bancária.</p>
      </FormSection>
      <FormSection title="Beneficiário do boleto">
        <div v-if="cobranca.beneficiario_documento" class="details-grid">
          <DetailValue label="Nome / Razão social" :value="cobranca.beneficiario_documento.nome" wide />
          <DetailValue label="CPF/CNPJ" :value="globalUtils.formatarDocumento(cobranca.beneficiario_documento.documento)" />
          <DetailValue label="Endereço preservado" :value="Object.values(cobranca.beneficiario_documento.endereco).filter(Boolean).join(' · ')" wide />
        </div>
        <p v-else class="text-muted mb-0">Identidade documental não comprovada. Não se completam títulos históricos com o cadastro atual. Consulta e cancelamento dependem da identidade bancária original.</p>
      </FormSection>

      <FormSection title="Configuração bancária">
        <div class="details-grid">
          <DetailValue
            label="Conta financeira"
            :value="cobranca.conta_financeira_apelido"
          />

          <DetailValue
            label="Instituição"
            :value="cobranca.conta_financeira_instituicao"
          />

          <DetailValue
            label="Provedor"
            :value="cobranca.provedor"
          />

          <DetailValue
            label="Adaptador"
            :value="cobranca.adaptador"
          />

          <DetailValue
            label="Ambiente"
            :value="cobranca.ambiente"
          />

          <DetailValue
            label="Boleto + PIX permitido"
            :value="yesNo(cobranca.permite_boleto_pix)"
          />

          <DetailValue
            label="Configuração ativa"
            :value="yesNo(cobranca.configuracao_ativa)"
          />

          <DetailValue
            label="Configuração"
            :value="idLabel(cobranca.configuracao_id)"
          />
        </div>
      </FormSection>

      <FormSection title="Boleto e PIX">
        <div
          v-if="boleto"
          class="details-grid"
        >
          <DetailValue
            label="Nosso número"
            :value="boleto.nosso_numero"
            mono
          />

          <DetailValue
            label="Seu número"
            :value="boleto.seu_numero"
            mono
          />

          <DetailValue
            label="Data limite"
            :value="formatDate(boleto.data_limite_pagamento)"
          />

          <DetailValue
            label="TXID PIX"
            :value="boleto.pix_txid"
            mono
          />

          <CopyableValue
            label="Linha digitável"
            :value="boleto.linha_digitavel"
            @copy="copyText"
          />

          <CopyableValue
            label="Código de barras"
            :value="boleto.codigo_barras"
            @copy="copyText"
          />

          <CopyableValue
            label="PIX copia e cola"
            :value="boleto.pix_copia_cola"
            @copy="copyText"
          />

          <div class="detail-item detail-item-documents">
            <span class="detail-label">
              Visualização e impressão
            </span>

            <div class="d-flex flex-wrap gap-2">
              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                @click="abrirVisualizadorDocumento('boleto')"
              >
                <i class="bi bi-receipt me-1"></i>
                Visualizar boleto
              </button>

              <button type="button" class="btn btn-outline-secondary btn-sm"
                @click="abrirVisualizadorDocumento('resumo')">Resumo da cobrança</button>

              <button
                v-if="boleto.pix_copia_cola"
                type="button"
                class="btn btn-outline-success btn-sm"
                @click="abrirVisualizadorDocumento('pix')"
              >
                <i class="bi bi-qr-code me-1"></i>
                Visualizar QR Code
              </button>

              <button
                v-if="documentosProvedorDisponiveis && isSafeUrl(boleto.url_pdf)"
                type="button"
                class="btn btn-outline-danger btn-sm"
                @click="openUrl(boleto.url_pdf)"
              >
                <i class="bi bi-file-earmark-pdf me-1"></i>
                Abrir PDF do provedor
              </button>

              <button
                v-if="documentosProvedorDisponiveis && isSafeUrl(boleto.url_boleto)"
                type="button"
                class="btn btn-outline-secondary btn-sm"
                @click="openUrl(boleto.url_boleto)"
              >
                <i class="bi bi-box-arrow-up-right me-1"></i>
                Abrir boleto do provedor
              </button>
            </div>

            <small
              v-if="cobranca.status === 'CANCELADA'"
              class="document-history-warning"
            >
              Documento disponível somente para histórico. A cobrança está cancelada.
            </small>
          </div>
        </div>

        <div
          v-else
          class="text-muted"
        >
          Nenhum boleto foi registrado para esta cobrança.
        </div>
      </FormSection>

      <FormSection title="Histórico de eventos">
        <div
          v-if="eventos.length"
          class="table-responsive"
        >
          <table class="table table-sm table-striped align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>Data</th>
                <th>Evento</th>
                <th>Status anterior</th>
                <th>Status novo</th>
                <th>Origem</th>
                <th>Identificador externo</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="evento in eventos"
                :key="evento.id"
              >
                <td class="text-nowrap">
                  {{ formatDateTime(evento.data_evento || evento.criado_em) }}
                </td>

                <td>
                  {{ eventLabel(evento.tipo_evento) }}
                </td>

                <td>
                  {{ statusLabel(evento.status_anterior) }}
                </td>

                <td>
                  {{ statusLabel(evento.status_novo) }}
                </td>

                <td>
                  {{ evento.origem_evento || '—' }}
                </td>

                <td>
                  {{ evento.identificador_externo || '—' }}
                </td>
              </tr>
            </tbody>
          </table>

          <div
            v-if="eventosTruncados"
            class="alert alert-warning mt-3 mb-0"
          >
            O histórico exibido foi limitado aos eventos mais recentes.
          </div>
        </div>

        <div
          v-else
          class="text-muted"
        >
          Nenhum evento foi registrado para esta cobrança.
        </div>
      </FormSection>
    </template>

    <Teleport to="body">
      <div
        v-if="emailModalAberto"
        class="email-preview-backdrop"
        role="presentation"
        @click.self="fecharPreviewEmail"
      >
        <section
          class="email-preview-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-preview-title"
        >
          <header class="email-preview-header">
            <div>
              <h5
                id="email-preview-title"
                class="mb-1"
              >
                Pré-visualização do e-mail
              </h5>

              <small class="text-muted">
                Confira o destinatário e o conteúdo antes de enviar.
              </small>
            </div>

            <button
              type="button"
              class="btn-close"
              aria-label="Fechar"
              :disabled="enviandoEmail"
              @click="fecharPreviewEmail"
            ></button>
          </header>

          <div
            v-if="emailPreview"
            class="email-preview-body"
          >
            <div class="email-preview-summary">
              <div class="email-preview-field email-preview-field-wide">
                <span>Destinatário</span>
                <strong>{{ emailPreview.destinatario?.nome || '—' }}</strong>
                <small>{{ emailPreview.destinatario?.email || '—' }}</small>
              </div>

              <div class="email-preview-field">
                <span>Vínculo</span>
                <strong>{{ emailRecipientOriginLabel(emailPreview.destinatario?.origem) }}</strong>
              </div>

              <div class="email-preview-field">
                <span>Aluno</span>
                <strong>{{ emailPreview.resumo?.aluno_nome || '—' }}</strong>
              </div>

              <div class="email-preview-field">
                <span>Valor</span>
                <strong>{{ formatMoney(emailPreview.resumo?.valor) }}</strong>
              </div>

              <div class="email-preview-field">
                <span>Vencimento</span>
                <strong>{{ formatDate(emailPreview.resumo?.vencimento) }}</strong>
              </div>

              <div class="email-preview-field email-preview-field-wide">
                <span>Assunto</span>
                <strong>{{ emailPreview.assunto || '—' }}</strong>
              </div>
            </div>

            <div
              v-if="emailPreview.resumo?.possui_boleto || emailPreview.resumo?.possui_pix"
              class="email-preview-actions"
            >
              <div>
                <strong>Documentos da cobrança</strong>
                <small>
                  Os botões abaixo abrem o visualizador interno. Em ambiente MOCK, os links bancários fictícios não são incluídos no e-mail.
                </small>
              </div>

              <div class="email-preview-action-buttons">
                <button
                  v-if="emailPreview.resumo?.possui_boleto"
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  :disabled="enviandoEmail"
                  @click="abrirDocumentoDoEmail('boleto')"
                >
                  <i class="bi bi-file-earmark-text me-1"></i>
                  Visualizar boleto
                </button>

                <button
                  v-if="emailPreview.resumo?.possui_pix"
                  type="button"
                  class="btn btn-outline-success btn-sm"
                  :disabled="enviandoEmail"
                  @click="abrirDocumentoDoEmail('pix')"
                >
                  <i class="bi bi-qr-code me-1"></i>
                  Visualizar QR Code PIX
                </button>
              </div>
            </div>

            <div class="email-preview-document">
              <iframe
                title="Conteúdo do e-mail da cobrança"
                :srcdoc="emailPreview.corpo_html"
                sandbox="allow-popups allow-popups-to-escape-sandbox"
              ></iframe>
            </div>
          </div>

          <footer class="email-preview-footer">
            <button
              type="button"
              class="btn btn-outline-secondary"
              :disabled="enviandoEmail"
              @click="fecharPreviewEmail"
            >
              Cancelar
            </button>

            <button
              type="button"
              class="btn btn-primary"
              :disabled="enviandoEmail || !emailPreview"
              @click="enviarEmailCobranca"
            >
              <span
                v-if="enviandoEmail"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>

              <i
                v-else
                class="bi bi-envelope-arrow-up me-1"
              ></i>

              {{ enviandoEmail ? 'Enviando...' : 'Confirmar envio' }}
            </button>
          </footer>
        </section>
      </div>
    </Teleport>

    <VisualizadorDocumentoCobranca
      :visible="visualizadorDocumentoAberto"
      :cobranca="cobranca"
      :boleto="boleto"
      :initial-mode="visualizadorModoInicial"
      @close="fecharVisualizadorDocumento"
      @copy="copiarDoVisualizador"
    />
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  h
} from 'vue';

const DetailValue =
  defineComponent({
    name: 'DetailValue',

    props: {
      label: {
        type: String,
        required: true
      },

      value: {
        type: [
          String,
          Number,
          Boolean
        ],

        default: null
      },

      wide: {
        type: Boolean,
        default: false
      },

      mono: {
        type: Boolean,
        default: false
      },

      emphasis: {
        type: Boolean,
        default: false
      }
    },

    setup(props) {
      return () =>
        h(
          'div',
          {
            class: [
              'detail-item',

              {
                'detail-item-wide':
                  props.wide,

                'detail-item-mono':
                  props.mono,

                'detail-item-emphasis':
                  props.emphasis
              }
            ]
          },
          [
            h(
              'span',
              {
                class:
                  'detail-label'
              },
              props.label
            ),

            h(
              'strong',
              {
                class:
                  'detail-value'
              },
              props.value === null ||
              props.value === undefined ||
              props.value === ''
                ? '—'
                : String(
                    props.value
                  )
            )
          ]
        );
    }
  });

const CopyableValue =
  defineComponent({
    name: 'CopyableValue',

    emits: ['copy'],

    props: {
      label: {
        type: String,
        required: true
      },

      value: {
        type: String,
        default: ''
      }
    },

    setup(props, { emit }) {
      const hasValue =
        computed(
          () =>
            Boolean(
              String(
                props.value || ''
              ).trim()
            )
        );

      return () =>
        h(
          'div',
          {
            class:
              'detail-item detail-item-wide detail-item-code'
          },
          [
            h(
              'span',
              {
                class:
                  'detail-label'
              },
              props.label
            ),

            h(
              'div',
              {
                class:
                  'copy-row'
              },
              [
                h(
                  'code',
                  {
                    class: [
                      'copy-value',

                      {
                        'copy-value-empty':
                          !hasValue.value
                      }
                    ]
                  },
                  hasValue.value
                    ? String(
                        props.value
                      )
                    : '—'
                ),

                hasValue.value
                  ? h(
                      'button',
                      {
                        type:
                          'button',

                        class:
                          'btn btn-outline-secondary btn-sm copy-button',

                        title:
                          `Copiar ${props.label}`,

                        'aria-label':
                          `Copiar ${props.label}`,

                        onClick:
                          () =>
                            emit(
                              'copy',
                              String(
                                props.value
                              )
                            )
                      },
                      [
                        h(
                          'i',
                          {
                            class:
                              'bi bi-copy',

                            'aria-hidden':
                              'true'
                          }
                        )
                      ]
                    )
                  : null
              ]
            )
          ]
        );
    }
  });

export default {
  components: {
    DetailValue,
    CopyableValue
  }
};
</script>

<script setup>
import { useBoletosOperacionais } from '@/composables/useBoletosOperacionais';
import {
  computed as setupComputed,
  getCurrentInstance,
  onMounted,
  watch,
  ref
} from 'vue';

import {
  useRoute,
  useRouter
} from 'vue-router';

import FormSection from '@/components/Layout/FormSection.vue';
import VisualizadorDocumentoCobranca from '@/components/Financeiro/Cobrancas/VisualizadorDocumentoCobranca.vue';
import cobrancasApi from '@/services/cobrancas.api';
import globalUtils from '@/utils/globalUtils';
import {
  usePermissions
} from '@/composables/usePermissions';

const route =
  useRoute();

const router =
  useRouter();

const proxy =
  getCurrentInstance()?.proxy;

const {
  fetchPermissions,
  hasPermission
} = usePermissions();

const reemissao = ref(null);
const reemissaoModalAberto = ref(false);
const reemitindo = ref(false);
const novoVencimento = ref('');
const motivoReemissao = ref('');
const erroReemissao = ref('');
const feedbackReemissao = ref('');
const exibirAcaoReemissao = setupComputed(() => podeOperar.value && ['REEMITIR', 'CRIAR_SUBSTITUTA'].includes(reemissao.value?.acao));
const podeReemitir = setupComputed(() => exibirAcaoReemissao.value && reemissao.value?.permitida === true);
const textoReemissao = setupComputed(() => reemissao.value?.acao === 'CRIAR_SUBSTITUTA' ? 'Criar boleto substituto' : 'Reemitir boleto');
function abrirReemissao() {
  if (!podeReemitir.value || operacaoEmAndamento.value || loading.value) return;
  novoVencimento.value = '';
  motivoReemissao.value = '';
  erroReemissao.value = '';
  reemissaoModalAberto.value = true;
}
async function confirmarReemissao() {
  if (!podeReemitir.value || operacaoEmAndamento.value) return;
  if (!novoVencimento.value || novoVencimento.value < reemissao.value.data_atual) {
    erroReemissao.value = 'Informe um novo vencimento igual ou posterior à data atual.';
    return;
  }
  reemitindo.value = true;
  erroReemissao.value = '';
  try {
    const result = await cobrancasApi.reemitir(cobranca.value.id, {
      novo_vencimento: novoVencimento.value, motivo: motivoReemissao.value || undefined
    });
    reemissaoModalAberto.value = false;
    if (result.pendente) {
      feedbackReemissao.value = result.mensagem;
      await carregarDetalhe();
    } else {
      await router.push({ name: 'DetalharCobranca', params: { id: result.cobranca.id } });
      feedbackReemissao.value = 'Nova cobrança criada em rascunho.';
    }
  } catch (error) {
    erroReemissao.value = apiErrorMessage(error, 'Não foi possível reemitir o boleto.');
    await carregarDetalhe();
  } finally { reemitindo.value = false; }
}
watch(() => route.params.id, async () => {
  reemissaoModalAberto.value = false;
  feedbackReemissao.value = '';
  await carregarDetalhe();
});

const loading = ref(true);
const registrando = ref(false);
const cancelando = ref(false);
const carregandoPreviewEmail = ref(false);
const enviandoEmail = ref(false);
const emailModalAberto = ref(false);
const emailPreview = ref(null);
const visualizadorDocumentoAberto = ref(false);
const visualizadorModoInicial = ref('boleto');
const errorMessage = ref('');

const cobranca = ref(null);
const boleto = ref(null);
const eventos = ref([]);
const eventosTruncados = ref(false);

const STATUS_REGISTRAVEIS =
  new Set([
    'RASCUNHO',
    'ERRO'
  ]);

const boletosOperacionais = useBoletosOperacionais();
const podeOperar =
  setupComputed(() =>
    hasPermission(
      'financeiro_cobrancas_operar'
    ) && boletosOperacionais.value
  );

const podeRegistrar =
  setupComputed(() =>
    podeOperar.value &&
    STATUS_REGISTRAVEIS.has(
      cobranca.value?.status
    )
  );

const textoBotaoRegistro =
  setupComputed(() =>
    registrando.value
      ? 'Registrando...'
      : cobranca.value?.status ===
          'ERRO'
        ? 'Tentar registrar novamente'
        : 'Registrar cobrança'
  );

const podeEnviarEmail =
  setupComputed(() =>
    podeOperar.value &&
    cobranca.value?.status ===
      'REGISTRADA'
  );

const podeCancelar =
  setupComputed(() =>
    podeOperar.value &&
    cobranca.value?.status ===
      'REGISTRADA'
  );

const documentosProvedorDisponiveis =
  setupComputed(() =>
    String(
      cobranca.value?.ambiente ||
      ''
    )
      .trim()
      .toUpperCase() !==
    'MOCK'
  );

const operacaoEmAndamento =
  setupComputed(() =>
    reemitindo.value ||
    registrando.value ||
    cancelando.value ||
    carregandoPreviewEmail.value ||
    enviandoEmail.value
  );

const textoBotaoCancelamento =
  setupComputed(() =>
    cancelando.value
      ? 'Cancelando...'
      : 'Cancelar cobrança'
  );

const textoBotaoEmail =
  setupComputed(() =>
    carregandoPreviewEmail.value
      ? 'Preparando e-mail...'
      : 'Enviar por e-mail'
  );

const statusLabels = {
  RASCUNHO: 'Rascunho',
  REGISTRO_PENDENTE:
    'Registro pendente',
  REGISTRADA: 'Registrada',
  REJEITADA: 'Rejeitada',
  VENCIDA: 'Vencida',
  ALTERACAO_PENDENTE:
    'Alteração pendente',
  BAIXA_PENDENTE:
    'Baixa pendente',
  BAIXADA: 'Baixada',
  LIQUIDADA: 'Liquidada',
  CREDITADA: 'Creditada',
  ESTORNADA: 'Estornada',
  CANCELADA: 'Cancelada',
  SUBSTITUIDA: 'Substituída',
  ERRO: 'Erro'
};

function apiErrorMessage(
  error,
  fallback
) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

async function carregarDetalhe() {
  loading.value = true;
  errorMessage.value = '';
  reemissao.value = null;

  try {
    const result =
      await cobrancasApi.detalhar(
        route.params.id
      );

    cobranca.value =
      result?.cobranca ||
      null;

    reemissao.value = await cobrancasApi.consultarReemissao(route.params.id);

    boleto.value =
      result?.boleto ||
      null;

    eventos.value =
      Array.isArray(
        result?.eventos
      )
        ? result.eventos
        : [];

    eventosTruncados.value =
      result?.eventos_truncados ===
      true;

    if (!cobranca.value) {
      throw new Error(
        'A cobrança não foi retornada pelo servidor.'
      );
    }
  } catch (error) {
    console.error(
      'Erro ao carregar cobrança:',
      error
    );

    // Sem uma consulta válida, o estado anterior não pode autorizar ações.
    cobranca.value = null;
    boleto.value = null;
    eventos.value = [];
    eventosTruncados.value = false;

    errorMessage.value =
      apiErrorMessage(
        error,
        'Não foi possível carregar a cobrança.'
      );
  } finally {
    loading.value = false;
  }
}

async function mostrarAlerta(
  mensagem,
  titulo
) {
  if (proxy?.$alert) {
    await proxy.$alert(
      mensagem,
      titulo
    );

    return;
  }

  window.alert(
    `${titulo}: ${mensagem}`
  );
}

async function capturarBeneficiario() {
  if (!window.confirm('Confirma a revisão cadastral do beneficiário e seu vínculo com o convênio? Somente rascunhos sem tentativa externa podem receber captura prospectiva.')) return;
  try {
    await cobrancasApi.capturarBeneficiario(cobranca.value.id);
    await carregarDetalhe();
  } catch (error) { window.alert(apiErrorMessage(error, 'Não foi possível preservar o beneficiário.')); }
}

async function registrarCobranca() {
  if (
    !podeRegistrar.value ||
    reemitindo.value ||
    registrando.value ||
    !cobranca.value?.id
  ) {
    return;
  }

  const confirmou =
    window.confirm(
      `Registrar a cobrança #${cobranca.value.id}?

` +
      'Esta ação enviará a cobrança ao provedor configurado.'
    );

  if (!confirmou) {
    return;
  }

  registrando.value = true;

  try {
    const resultado =
      await cobrancasApi.registrar(
        cobranca.value.id
      );

    await carregarDetalhe();

    await mostrarAlerta(
      resultado?.idempotente === true
        ? 'A cobrança consta como registrada.'
        : 'Cobrança registrada com sucesso.',
      'Cobrança'
    );
  } catch (error) {
    console.error(
      'Erro ao registrar cobrança:',
      error
    );

    // Uma falha de transporte pode ter deixado o registro pendente no banco.
    // Atualize o estado antes de liberar novamente as ações da tela.
    await carregarDetalhe();

    await mostrarAlerta(
      cobranca.value?.status === 'REGISTRO_PENDENTE'
        ? 'A confirmação do registro está pendente. Aguarde a recuperação antes de tentar emitir novamente.'
        : cobranca.value?.status === 'REGISTRADA'
          ? 'A cobrança consta como registrada após a consulta de verificação.'
          : apiErrorMessage(
            error,
            'Não foi possível registrar a cobrança.'
          ),
      cobranca.value?.status === 'REGISTRADA' ? 'Cobrança' : 'Erro'
    );
  } finally {
    registrando.value = false;
  }
}

async function cancelarCobranca() {
  if (
    !podeCancelar.value ||
    operacaoEmAndamento.value ||
    !cobranca.value?.id
  ) {
    return;
  }

  const identificacao =
    cobranca.value
      .numero_contrato
      ? ` do contrato ${cobranca.value.numero_contrato}`
      : '';

  const confirmou =
    window.confirm(
      `Cancelar a cobrança #${cobranca.value.id}${identificacao}?

` +
      'Esta ação solicitará a baixa ao provedor e invalidará o boleto e o PIX para novos pagamentos. ' +
      'As datas financeiras da parcela não serão alteradas automaticamente.'
    );

  if (!confirmou) {
    return;
  }

  cancelando.value = true;

  try {
    const resultado =
      await cobrancasApi.cancelar(
        cobranca.value.id
      );

    await carregarDetalhe();

    await mostrarFeedbackCancelamento(resultado);
  } catch (error) {
    console.error(
      'Erro ao cancelar cobrança:',
      error
    );

    /*
     * Atualiza o detalhe mesmo em falha. Caso o provedor tenha
     * confirmado a baixa, mas a conclusão local tenha falhado,
     * o backend preserva BAIXA_PENDENTE para revisão segura.
     */
    await carregarDetalhe();

    await mostrarFeedbackCancelamento(null, error);
  } finally {
    cancelando.value = false;
  }
}

async function mostrarFeedbackCancelamento(resultado, error = null) {
  const status =
    cobranca.value?.status ||
    resultado?.cobranca?.status;

  if (status === 'CANCELADA') {
    const conclusaoEfetiva =
      resultado?.idempotente !== true &&
      resultado?.confirmada === true &&
      resultado?.cancelada === true &&
      resultado?.pendente !== true;

    await mostrarAlerta(
      conclusaoEfetiva
        ? 'Cobrança cancelada com sucesso. O boleto e o PIX não devem mais ser utilizados.'
        : 'A cobrança consta como cancelada.',
      'Cobrança'
    );
    return;
  }

  if (status === 'BAIXA_PENDENTE' || resultado?.pendente === true) {
    await mostrarAlerta(
      'O cancelamento permanece pendente de confirmação.',
      'Cancelamento pendente'
    );
    return;
  }

  await mostrarAlerta(
    apiErrorMessage(error, 'Não foi possível confirmar o cancelamento da cobrança.'),
    'Erro'
  );
}

async function abrirPreviewEmail() {
  if (
    !podeEnviarEmail.value ||
    operacaoEmAndamento.value ||
    !cobranca.value?.id
  ) {
    return;
  }

  carregandoPreviewEmail.value = true;
  emailPreview.value = null;

  try {
    const preview =
      await cobrancasApi
        .obterPreviewEmail(
          cobranca.value.id
        );

    if (
      !preview?.destinatario?.email ||
      !preview?.corpo_html
    ) {
      throw new Error(
        'A pré-visualização retornada pelo servidor está incompleta.'
      );
    }

    emailPreview.value = preview;
    emailModalAberto.value = true;
  } catch (error) {
    console.error(
      'Erro ao preparar e-mail da cobrança:',
      error
    );

    await mostrarAlerta(
      apiErrorMessage(
        error,
        'Não foi possível preparar o e-mail da cobrança.'
      ),
      'Erro'
    );
  } finally {
    carregandoPreviewEmail.value = false;
  }
}

function abrirDocumentoDoEmail(
  mode
) {
  if (
    enviandoEmail.value ||
    !emailPreview.value
  ) {
    return;
  }

  abrirVisualizadorDocumento(
    mode === 'pix'
      ? 'pix'
      : 'boleto'
  );
}

function fecharPreviewEmail() {
  if (enviandoEmail.value) {
    return;
  }

  emailModalAberto.value = false;
  emailPreview.value = null;
}

async function enviarEmailCobranca() {
  if (
    enviandoEmail.value ||
    !emailPreview.value ||
    !cobranca.value?.id
  ) {
    return;
  }

  const destinatario =
    emailPreview.value
      .destinatario;

  const confirmou =
    window.confirm(
      `Enviar a cobrança #${cobranca.value.id} por e-mail para ${destinatario.nome} <${destinatario.email}>?`
    );

  if (!confirmou) {
    return;
  }

  enviandoEmail.value = true;

  try {
    const resultado =
      await cobrancasApi
        .enviarEmail(
          cobranca.value.id
        );

    emailModalAberto.value = false;
    emailPreview.value = null;

    await carregarDetalhe();

    await mostrarAlerta(
      resultado?.auditoria_registrada ===
        false
        ? 'O e-mail foi enviado, mas o registro final de auditoria não pôde ser confirmado. Verifique o histórico e os logs.'
        : 'Cobrança enviada por e-mail com sucesso.',
      resultado?.auditoria_registrada ===
        false
        ? 'Envio concluído com alerta'
        : 'Cobrança'
    );
  } catch (error) {
    console.error(
      'Erro ao enviar cobrança por e-mail:',
      error
    );

    await mostrarAlerta(
      apiErrorMessage(
        error,
        'Não foi possível enviar a cobrança por e-mail.'
      ),
      'Erro'
    );
  } finally {
    enviandoEmail.value = false;
  }
}

function emailRecipientOriginLabel(value) {
  if (value === 'RESPONSAVEL') {
    return 'Responsável';
  }

  if (value === 'ALUNO') {
    return 'Aluno';
  }

  return value || '—';
}

function voltar() {
  router.push(
    '/financeiro/cobrancas'
  );
}

function formatMoney(value) {
  return globalUtils.formatarValor(
    Number(value ?? 0)
  );
}

function optionalMoney(value) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '—';
  }

  return formatMoney(value);
}

function formatDate(value) {
  return (
    globalUtils.formatarData(
      value
    ) ||
    '—'
  );
}

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return formatDate(value);
  }

  return date.toLocaleString(
    'pt-BR'
  );
}

function formatInstrument(value) {
  if (value === 'BOLETO_PIX') {
    return 'Boleto + PIX';
  }

  if (value === 'BOLETO') {
    return 'Boleto';
  }

  return value || '—';
}

function statusLabel(value) {
  if (!value) {
    return '—';
  }

  return (
    statusLabels[value] ||
    String(value)
      .replaceAll('_', ' ')
  );
}

function statusClass(value) {
  if (
    [
      'REGISTRADA',
      'LIQUIDADA',
      'CREDITADA'
    ].includes(value)
  ) {
    return 'status-success';
  }

  if (
    [
      'RASCUNHO',
      'REGISTRO_PENDENTE',
      'ALTERACAO_PENDENTE',
      'BAIXA_PENDENTE'
    ].includes(value)
  ) {
    return 'status-warning';
  }

  if (
    [
      'REJEITADA',
      'VENCIDA',
      'ESTORNADA',
      'CANCELADA',
      'ERRO'
    ].includes(value)
  ) {
    return 'status-danger';
  }

  return 'status-secondary';
}

function originLabel(value) {
  if (
    value ===
    'pagamentos_matriculas'
  ) {
    return 'Mensalidade';
  }

  if (
    value ===
    'matriculas_pagamentos'
  ) {
    return 'Pagamento da matrícula';
  }

  return value || '—';
}

function eventLabel(value) {
  return String(value || '—')
    .replaceAll('_', ' ');
}

function idLabel(value) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '—';
  }

  return `#${value}`;
}

function yesNo(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return '—';
  }

  return value
    ? 'Sim'
    : 'Não';
}

function isSafeUrl(value) {
  if (!value) {
    return false;
  }

  try {
    const parsed =
      new URL(
        value,
        window.location.origin
      );

    return [
      'http:',
      'https:'
    ].includes(
      parsed.protocol
    );
  } catch {
    return false;
  }
}

function openUrl(value) {
  if (!isSafeUrl(value)) {
    return;
  }

  window.open(
    value,
    '_blank',
    'noopener,noreferrer'
  );
}

function abrirVisualizadorDocumento(
  modo = 'boleto'
) {
  visualizadorModoInicial.value =
    ['pix', 'resumo'].includes(modo) ? modo : 'boleto';

  visualizadorDocumentoAberto.value =
    true;
}

function fecharVisualizadorDocumento() {
  visualizadorDocumentoAberto.value =
    false;
}

async function copiarDoVisualizador({
  value,
  label
} = {}) {
  await copyText(value);

  if (value && label) {
    console.info(
      `${label} copiado pelo visualizador da cobrança.`
    );
  }
}

async function copyText(value) {
  const text =
    String(value || '');

  if (!text) {
    return;
  }

  try {
    await navigator.clipboard
      .writeText(text);

    await proxy?.$alert?.(
      'Conteúdo copiado.',
      'Cobrança'
    );
  } catch (error) {
    console.error(
      'Erro ao copiar conteúdo:',
      error
    );

    await proxy?.$alert?.(
      'Não foi possível copiar o conteúdo.',
      'Erro'
    );
  }
}

onMounted(
  async () => {
    await fetchPermissions();

    if (
      !hasPermission(
        'financeiro_cobrancas_visualizar'
      )
    ) {
      await proxy?.$alert?.(
        'Você não possui permissão para visualizar cobranças.',
        'Permissão'
      );

      router.replace(
        '/dashboard'
      );

      return;
    }

    await carregarDetalhe();
  }
);
</script>

<style scoped>
.email-preview-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1080;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.66);
  backdrop-filter: blur(2px);
}

.email-preview-modal {
  display: flex;
  flex-direction: column;
  width: min(980px, 100%);
  max-height: calc(100vh - 2.5rem);
  overflow: hidden;
  border-radius: 0.75rem;
  background: #ffffff;
  box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, 0.28);
}

.email-preview-header,
.email-preview-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-color: #dee2e6;
  background: #ffffff;
}

.email-preview-header {
  border-bottom: 1px solid #dee2e6;
}

.email-preview-footer {
  justify-content: flex-end;
  border-top: 1px solid #dee2e6;
}

.email-preview-body {
  min-height: 0;
  overflow-y: auto;
  padding: 1.25rem;
  background: #f8f9fa;
}

.email-preview-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.email-preview-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0.8rem 0.9rem;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  background: #ffffff;
}

.email-preview-field-wide {
  grid-column: span 2;
}

.email-preview-field span {
  margin-bottom: 0.35rem;
  color: #6c757d;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.035em;
  text-transform: uppercase;
}

.email-preview-field strong,
.email-preview-field small {
  overflow-wrap: anywhere;
}

.email-preview-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid #cfe2dc;
  border-radius: 0.55rem;
  background: #f0f7f5;
}

.email-preview-actions > div:first-child {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.email-preview-actions strong {
  color: #1f5f53;
  font-size: 0.88rem;
}

.email-preview-actions small {
  margin-top: 0.2rem;
  color: #596764;
  line-height: 1.35;
}

.email-preview-action-buttons {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
}

.email-preview-document {
  overflow: hidden;
  border: 1px solid #ced4da;
  border-radius: 0.6rem;
  background: #ffffff;
}

.email-preview-document iframe {
  display: block;
  width: 100%;
  min-height: 460px;
  border: 0;
  background: #ffffff;
}

.details-grid {
  display: grid;
  width: 100%;
  grid-template-columns:
    repeat(
      4,
      minmax(0, 1fr)
    );
  gap: 1rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 4.65rem;
  padding: 0.85rem 1rem;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  background: #ffffff;
  box-shadow:
    0 1px 2px
    rgba(0, 0, 0, 0.025);
}

.detail-item-wide {
  grid-column: span 2;
}

.detail-item-status {
  justify-content: center;
}

:deep(.detail-label) {
  display: block;
  margin: 0 0 0.45rem;
  color: #6c757d;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.035em;
  text-transform: uppercase;
}

:deep(.detail-value) {
  display: block;
  color: #212529;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.detail-item-emphasis
:deep(.detail-value) {
  color: #0f5132;
  font-size: 1rem;
  font-weight: 700;
}

.detail-item-mono
:deep(.detail-value) {
  font-family:
    ui-monospace,
    SFMono-Regular,
    Menlo,
    Monaco,
    Consolas,
    "Liberation Mono",
    "Courier New",
    monospace;
  font-size: 0.8rem;
  font-weight: 600;
  word-break: break-all;
}

:deep(.copy-row) {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  min-width: 0;
}

:deep(.copy-value) {
  display: block;
  flex: 1;
  min-width: 0;
  min-height: 2.5rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.4rem;
  background: #f8fafc;
  color: #212529;
  font-family:
    ui-monospace,
    SFMono-Regular,
    Menlo,
    Monaco,
    Consolas,
    "Liberation Mono",
    "Courier New",
    monospace;
  font-size: 0.78rem;
  line-height: 1.45;
  white-space: normal;
  overflow-wrap: anywhere;
  user-select: all;
}

:deep(.copy-value-empty) {
  color: #6c757d;
  font-family: inherit;
}

:deep(.copy-button) {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
}

.status-badge {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  min-height: 1.65rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
}

.status-success {
  color: #146c43;
  background: #d1e7dd;
}

.status-warning {
  color: #664d03;
  background: #fff3cd;
}

.status-danger {
  color: #842029;
  background: #f8d7da;
}

.status-secondary {
  color: #41464b;
  background: #e2e3e5;
}

.detail-item-documents {
  grid-column: span 2;
  justify-content: space-between;
}

.table-responsive {
  width: 100%;
  border: 1px solid #dee2e6;
  border-radius: 0.45rem;
}

.table {
  margin-bottom: 0;
}

.table th {
  padding: 0.65rem 0.75rem;
  color: #495057;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.table td {
  padding: 0.65rem 0.75rem;
  font-size: 0.8rem;
  vertical-align: middle;
}

.table td:last-child {
  max-width: 22rem;
  overflow-wrap: anywhere;
}

.document-history-warning {
  display: block;
  width: 100%;
  margin-top: 0.65rem;
  color: #991b1b;
  font-size: 0.78rem;
  font-weight: 600;
}

@media (max-width: 900px) {
  .details-grid {
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );
  }

  .detail-item-documents {
    grid-column: span 2;
  }
}

@media (max-width: 650px) {
  .details-grid {
    grid-template-columns: 1fr;
  }

  .detail-item-wide,
  .detail-item-documents {
    grid-column: auto;
  }

  :deep(.copy-row) {
    align-items: stretch;
  }

  :deep(.copy-button) {
    width: 2.75rem;
    height: auto;
    min-height: 2.75rem;
  }
}

@media (max-width: 767.98px) {
  .email-preview-backdrop {
    padding: 0;
  }

  .email-preview-modal {
    width: 100%;
    max-height: 100vh;
    min-height: 100vh;
    border-radius: 0;
  }

  .email-preview-header,
  .email-preview-footer,
  .email-preview-body {
    padding: 1rem;
  }

  .email-preview-summary {
    grid-template-columns: 1fr;
  }

  .email-preview-field-wide {
    grid-column: span 1;
  }

  .email-preview-footer {
    flex-direction: column-reverse;
  }

  .email-preview-footer .btn {
    width: 100%;
  }

  .email-preview-document iframe {
    min-height: 520px;
  }
}

</style>


@media (max-width: 720px) {
  .email-preview-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .email-preview-action-buttons {
    justify-content: flex-start;
  }
}
